import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Clock, CheckCircle, XCircle, DollarSign, User, MapPin,
  Package, Loader2, ArrowRight, Route, RefreshCw, Shield, AlertTriangle, StickyNote, Pencil
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPayments = () => {
  const { getAuthHeader, userType } = useAuth();
  // Auditors review sessions but must not see dollar amounts anywhere.
  const hideFinancials = userType === 'auditor';
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [sendingToPayouts, setSendingToPayouts] = useState({});
  const [declineModalOpen, setDeclineModalOpen] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [togglingReview, setTogglingReview] = useState(null);
  // Notify Silently — per-session checkbox; actions taken on the card
  // (approve/decline on Pending, decline/send-to-payouts on Ready to
  // Pay) skip the worker notification + email when checked.
  const [silentIds, setSilentIds] = useState(new Set());

  const toggleSilent = (sessionId) => {
    setSilentIds(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId); else next.add(sessionId);
      return next;
    });
  };

  // Silent Batch Approve — select multiple pending sessions, approve all with no worker notifications
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchApproving, setBatchApproving] = useState(false);

  const toggleSelected = (sessionId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId); else next.add(sessionId);
      return next;
    });
  };

  const handleBatchApproveSilently = async () => {
    if (selectedIds.size === 0) return;
    setBatchApproving(true);
    try {
      const res = await axios.put(`${API}/admin/sessions/batch-approve`, { session_ids: [...selectedIds], silent: true }, getAuthHeader());
      toast.success(`${res.data.approved} session(s) approved silently (no notifications sent)`);
      setSelectedIds(new Set());
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to batch approve');
    } finally { setBatchApproving(false); }
  };

  // Review notes — auditors leave a note on a session; admins see it before approving
  const [noteModal, setNoteModal] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const openNoteModal = (session) => {
    setNoteModal(session);
    setNoteText(session.review_note || '');
  };

  const handleSaveReviewNote = async (clear = false) => {
    setSavingNote(true);
    try {
      const res = await axios.put(
        `${API}/admin/sessions/${noteModal.id}/review-note`,
        { note: clear ? '' : noteText },
        getAuthHeader()
      );
      toast.success(res.data?.message || 'Review note saved');
      setNoteModal(null);
      setNoteText('');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save note');
    } finally { setSavingNote(false); }
  };

  const [refreshing, setRefreshing] = useState(false);
  // Dedup savings rollup — surfaces how much money the iter95 server-side
  // dedup has saved over the last 30 days + flags suspicious workers.
  const [dedupSavings, setDedupSavings] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [sessionsRes, savingsRes] = await Promise.all([
        axios.get(`${API}/admin/sessions`, getAuthHeader()),
        axios.get(`${API}/admin/sessions/dedup-savings?days=30`, getAuthHeader()).catch(() => null),
      ]);
      setSessions(sessionsRes.data);
      if (savingsRes?.data) setDedupSavings(savingsRes.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally { setLoading(false); setRefreshing(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleApproveSession = async (sessionId) => {
    const silent = silentIds.has(sessionId);
    try {
      await axios.put(`${API}/admin/sessions/${sessionId}/approve`, { silent }, getAuthHeader());
      toast.success(silent ? 'Session approved silently (no notification sent)' : 'Session approved');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve session');
    }
  };

  const handleSendToPayouts = async (sessionId) => {
    setSendingToPayouts(prev => ({ ...prev, [sessionId]: true }));
    try {
      const res = await axios.put(`${API}/admin/sessions/send-to-payouts`, {
        session_ids: [sessionId],
        silent_session_ids: silentIds.has(sessionId) ? [sessionId] : []
      }, getAuthHeader());
      toast.success(res.data?.message || 'Session sent to Payouts for payment processing');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to payouts');
    } finally {
      setSendingToPayouts(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const handleSendAllToPayouts = async () => {
    const sessionIds = approvedSessions.map(s => s.id);
    if (sessionIds.length === 0) return;
    setSendingToPayouts(prev => ({ ...prev, all: true }));
    try {
      const response = await axios.put(`${API}/admin/sessions/send-to-payouts`, {
        session_ids: sessionIds,
        silent_session_ids: sessionIds.filter(id => silentIds.has(id))
      }, getAuthHeader());
      if (response.data.skipped_count > 0) {
        toast.warning(response.data.message);
      } else {
        toast.success(response.data.message || `${response.data.count} sessions sent to Payouts`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to payouts');
    } finally {
      setSendingToPayouts(prev => ({ ...prev, all: false }));
    }
  };

  const handleDeclineSession = async () => {
    if (!declineReason.trim()) { toast.error('Please provide a reason'); return; }
    setDeclining(true);
    const silent = silentIds.has(declineModalOpen);
    try {
      await axios.put(`${API}/admin/sessions/${declineModalOpen}/decline`, { reason: declineReason, silent }, getAuthHeader());
      toast.success(silent ? 'Session declined silently (no notification sent)' : 'Session declined');
      setDeclineModalOpen(null);
      setDeclineReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline session');
    } finally { setDeclining(false); }
  };

  // "Decline" on the Ready-to-Pay tab — moves an already-approved
  // session back to the Pending queue (not into the Declined bucket),
  // so an auditor can re-review it before payout. Captures an optional
  // note via a modal so the next auditor knows what to re-check.
  const [revertingSession, setRevertingSession] = useState(null);
  const [revertModal, setRevertModal] = useState(null); // {session_id, job_title, user_name}
  const [revertNote, setRevertNote] = useState('');
  const handleOpenRevertModal = (sessionId, userName, jobTitle) => {
    setRevertModal({ session_id: sessionId, job_title: jobTitle, user_name: userName });
    setRevertNote('');
  };
  const handleConfirmRevert = async () => {
    if (!revertModal) return;
    const sessionId = revertModal.session_id;
    const silent = silentIds.has(sessionId);
    setRevertingSession(sessionId);
    try {
      await axios.put(
        `${API}/admin/sessions/${sessionId}/revert-to-pending`,
        { note: revertNote.trim() || null, silent },
        getAuthHeader(),
      );
      toast.success(
        silent
          ? 'Session moved back to Pending silently (no notification sent)'
          : revertNote.trim()
            ? 'Session moved back to Pending with auditor note'
            : 'Session moved back to Pending'
      );
      setRevertModal(null);
      setRevertNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert session');
    } finally { setRevertingSession(null); }
  };

  const handleToggleReviewed = async (sessionId, currentValue) => {
    setTogglingReview(sessionId);
    try {
      await axios.put(`${API}/admin/sessions/${sessionId}/auditor-reviewed`, { reviewed: !currentValue }, getAuthHeader());
      // Update local state immediately
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? { ...s, auditor_reviewed: !currentValue } : s
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update review status');
    } finally { setTogglingReview(null); }
  };

  // "Job Complete" checkbox — admin override that unlocks payouts below
  // 100% progress, marks the job completed, and removes it from
  // remaining-deliveries categorization. Applies to the JOB, so every
  // session card of that job reflects the toggle.
  const [togglingJobComplete, setTogglingJobComplete] = useState(null);
  const handleToggleJobComplete = async (session) => {
    const next = !session.job_complete;
    setTogglingJobComplete(session.id);
    try {
      const res = await axios.put(`${API}/admin/jobs/${session.job_id}/job-complete`, { complete: next }, getAuthHeader());
      setSessions(prev => prev.map(s =>
        s.job_id === session.job_id ? { ...s, job_complete: next } : s
      ));
      toast.success(res.data?.message || (next ? 'Job marked complete' : 'Job complete override removed'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job');
    } finally { setTogglingJobComplete(null); }
  };

  // Payout eligibility — job-wide delivered (all workers) must hit the
  // target, unless the admin checked "Job Complete" on the job.
  const isPayoutEligible = (session) => {
    if (session.job_complete) return true;
    const target = session.job_deliveries || 0;
    if (target <= 0) return true;
    return (session.job_delivered_total || 0) >= target;
  };

  const pendingSessions = sessions.filter(s => s.status === 'pending_approval');
  const approvedSessions = sessions.filter(s => s.status === 'approved');
  const declinedSessions = sessions.filter(s => s.status === 'declined');
  const completedSessions = sessions.filter(s => ['paid', 'pending_payout', 'completed'].includes(s.status));

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_approval':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending Approval</Badge>;
      case 'approved':
        return <Badge className="bg-blue-100 text-blue-700">Ready to Pay</Badge>;
      case 'pending_payout':
        return <Badge className="bg-purple-100 text-purple-700">Sent to Payouts</Badge>;
      case 'paid':
        return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
      case 'declined':
        return <Badge className="bg-red-100 text-red-700">Declined</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  // Two-tone bar: this worker's deliveries + a sky segment for what
  // OTHER users delivered on the same job. The second segment only
  // renders when a team actually worked this job (backend gates
  // job_other_users_delivered to multi-contributor jobs).
  const ProgressBar = ({ session }) => {
    const target = session.job_deliveries || 0;
    const worker = session.deliveries_count || 0;
    const others = session.job_other_users_delivered || 0;
    const jobTotal = Math.max(session.job_delivered_total || 0, worker);
    const workerPct = target > 0 ? Math.min(100, (worker / target) * 100) : 0;
    const othersPct = target > 0 ? Math.max(0, Math.min(100 - workerPct, (others / target) * 100)) : 0;
    const totalPct = target > 0 ? Math.min(100, Math.round((jobTotal / target) * 100)) : 0;
    return (
      <div className="mt-3 mb-1" data-testid={`progress-bar-${session.id}`}>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-gray-500">Delivery Progress</span>
          <span className="font-semibold text-gray-700">
            {worker}/{target} ({Math.round(workerPct)}%)
            {others > 0 && (
              <span className="text-sky-600 font-medium" data-testid={`others-count-${session.id}`}>
                {' '}· +{others} other users · job {totalPct}%
              </span>
            )}
          </span>
        </div>
        {othersPct >= 8 && (
          <div className="relative h-3.5">
            <span
              className="absolute text-[9px] leading-3 text-sky-600 font-semibold text-center truncate"
              style={{ left: `${workerPct}%`, width: `${othersPct}%` }}
              data-testid={`others-label-${session.id}`}
            >
              other users
            </span>
          </div>
        )}
        <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden flex">
          <div
            className={`h-full transition-all ${workerPct >= 100 ? 'bg-emerald-500' : workerPct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
            style={{ width: `${workerPct}%` }}
          />
          {othersPct > 0 && (
            <div
              className="h-full bg-sky-400 transition-all"
              style={{ width: `${othersPct}%` }}
              data-testid={`others-segment-${session.id}`}
            />
          )}
        </div>
      </div>
    );
  };

  const TrackAndReview = ({ session }) => (
    <div className="flex items-center gap-3 flex-wrap">
      <Button
        variant="outline"
        size="sm"
        className="text-green-600 border-green-300 hover:bg-green-50"
        onClick={() => window.open(`/track/${session.job_id}`, '_blank')}
        data-testid={`track-session-${session.id}`}
      >
        <Route size={16} className="mr-1" />
        Track
      </Button>
      <label
        className="flex items-center gap-2 cursor-pointer select-none"
        data-testid={`review-checkbox-label-${session.id}`}
      >
        <Checkbox
          checked={!!session.auditor_reviewed}
          onCheckedChange={() => handleToggleReviewed(session.id, !!session.auditor_reviewed)}
          disabled={togglingReview === session.id}
          data-testid={`review-checkbox-${session.id}`}
        />
        <span className={`text-xs font-medium ${session.auditor_reviewed ? 'text-green-700' : 'text-gray-500'}`}>
          Manager has reviewed tracking
        </span>
      </label>
      {session.status === 'approved' && userType !== 'auditor' && (
        <label
          className="flex items-center gap-2 cursor-pointer select-none"
          title="Marks the job as completed: unlocks Send to Payouts below 100% and removes it from remaining-deliveries tracking"
          data-testid={`job-complete-label-${session.id}`}
        >
          <Checkbox
            checked={!!session.job_complete}
            onCheckedChange={() => handleToggleJobComplete(session)}
            disabled={togglingJobComplete === session.id}
            data-testid={`job-complete-checkbox-${session.id}`}
          />
          <span className={`text-xs font-medium ${session.job_complete ? 'text-emerald-700' : 'text-gray-500'}`}>
            Job Complete
          </span>
        </label>
      )}
      {session.status === 'approved' && userType !== 'auditor' && (
        <Button
          variant="outline"
          size="sm"
          className="text-purple-600 border-purple-300 hover:bg-purple-50"
          onClick={() => window.open(`/admin/jobs/${session.job_id}/mark`, '_blank')}
          title="Open Admin Mark for this job"
          data-testid={`admin-mark-${session.id}`}
        >
          <Pencil size={15} className="mr-1" />
          Admin Mark
        </Button>
      )}
      {session.status === 'pending_approval' && (
        <Button
          variant="outline"
          size="sm"
          className={session.review_note
            ? 'text-indigo-700 border-indigo-300 bg-indigo-50 hover:bg-indigo-100'
            : 'text-gray-500 border-gray-300 hover:bg-gray-50'}
          onClick={() => openNoteModal(session)}
          data-testid={`review-note-btn-${session.id}`}
          title={session.review_note ? 'View / edit the review note' : 'Leave a note for the admin to see before approving'}
        >
          <StickyNote size={15} className="mr-1" />
          {session.review_note ? 'Edit Note' : 'Add Note'}
        </Button>
      )}
    </div>
  );

  const SessionCard = ({ session, showActions = false, showDeclineInfo = false }) => (
    <Card key={session.id} className="p-4 border-0 shadow-sm" data-testid={`session-card-${session.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          {showActions && (
            <input
              type="checkbox"
              checked={selectedIds.has(session.id)}
              onChange={() => toggleSelected(session.id)}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
              title="Select for silent batch approve"
              data-testid={`select-session-${session.id}`}
            />
          )}
          <div>
            <h3 className="font-semibold text-gray-900">{session.job_title}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
              <User size={14} />
              <span>{session.user_name}</span>
              <span className="text-gray-300">|</span>
              <span>{session.user_email}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(session.status)}
          {session.auditor_reviewed && (
            <Badge className="bg-green-100 text-green-700 text-xs">
              <CheckCircle size={10} className="mr-0.5" /> Reviewed
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 bg-gray-50 rounded-lg p-3 mb-2">
        <div className="text-center">
          <Package size={18} className="mx-auto text-orange-500 mb-1" />
          <p className="font-bold text-gray-900">{session.deliveries_count}</p>
          <p className="text-xs text-gray-500">Deliveries</p>
          {session.dedup_dropped_count > 0 && (
            <p
              className="text-[10px] text-emerald-700 font-semibold mt-1"
              title={hideFinancials
                ? `Mobile claimed ${session.client_claimed_count} but server dropped ${session.dedup_dropped_count} duplicate(s)`
                : `Mobile claimed ${session.client_claimed_count} but server dropped ${session.dedup_dropped_count} duplicate(s) — saved $${(session.dedup_savings_amount || 0).toFixed(2)}`}
              data-testid={`dedup-chip-${session.id}`}
            >
              −{session.dedup_dropped_count} dupe{hideFinancials ? '' : ` · $${(session.dedup_savings_amount || 0).toFixed(2)} saved`}
            </p>
          )}
        </div>
        <div className="text-center">
          <MapPin size={18} className="mx-auto text-blue-500 mb-1" />
          <p className="font-bold text-gray-900">{session.distance.toFixed(2)} mi</p>
          <p className="text-xs text-gray-500">Distance</p>
        </div>
        <div className="text-center">
          <Clock size={18} className="mx-auto text-purple-500 mb-1" />
          <p className="font-bold text-gray-900">{formatDuration(session.duration)}</p>
          <p className="text-xs text-gray-500">Duration</p>
        </div>
      </div>

      <ProgressBar session={session} />

      <div className="flex items-center justify-between mt-3">
        <TrackAndReview session={session} />

        {showActions && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <label
              className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none mr-1"
              title="No notification or email is sent to the worker for this approval or decline"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={silentIds.has(session.id)}
                onChange={() => toggleSilent(session.id)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                data-testid={`silent-approve-checkbox-${session.id}`}
              />
              Notify Silently
            </label>
            <Button size="sm" onClick={() => handleApproveSession(session.id)} className="bg-green-600 hover:bg-green-500" data-testid={`approve-session-${session.id}`}>
              <CheckCircle size={14} className="mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setDeclineModalOpen(session.id); setDeclineReason(''); }} className="text-red-500 border-red-200 hover:bg-red-50" data-testid={`decline-session-${session.id}`}>
              <XCircle size={14} className="mr-1" /> Decline
            </Button>
          </div>
        )}

        {session.status === 'approved' && userType !== 'auditor' && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <label
              className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none mr-1"
              title="No notification or email is sent to the worker for this decline or send-to-payouts"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={silentIds.has(session.id)}
                onChange={() => toggleSilent(session.id)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                data-testid={`silent-notify-checkbox-${session.id}`}
              />
              Notify Silently
            </label>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleOpenRevertModal(session.id, session.user_name, session.job_title)}
              disabled={revertingSession === session.id || sendingToPayouts[session.id]}
              className="text-red-500 border-red-200 hover:bg-red-50"
              data-testid={`revert-session-${session.id}`}
              title="Move this session back to Pending Approval for re-review"
            >
              {revertingSession === session.id
                ? <Loader2 size={14} className="animate-spin mr-1" />
                : <XCircle size={14} className="mr-1" />
              }
              Decline
            </Button>
            <span title={!isPayoutEligible(session)
              ? 'Delivery progress must reach 100% — or check "Job Complete" — before sending to payouts'
              : undefined}>
              <Button
                size="sm"
                onClick={() => handleSendToPayouts(session.id)}
                disabled={sendingToPayouts[session.id] || revertingSession === session.id || !isPayoutEligible(session)}
                className="bg-purple-600 hover:bg-purple-500"
                data-testid={`send-payout-${session.id}`}
              >
                {sendingToPayouts[session.id] ? <Loader2 size={14} className="animate-spin mr-1" /> : <ArrowRight size={14} className="mr-1" />}
                Send to Payouts
              </Button>
            </span>
          </div>
        )}
      </div>

      {showDeclineInfo && session.decline_reason && (
        <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-100">
          <p className="text-sm text-red-700"><strong>Reason:</strong> {session.decline_reason}</p>
          {session.declined_by_name && (
            <p className="text-xs text-red-500 mt-1">Declined by {session.declined_by_name}</p>
          )}
        </div>
      )}

      {/* Indigo review-note banner — auditor's note for the admin to read
          before approving. Distinct from the amber revert banner below. */}
      {session.status === 'pending_approval' && session.review_note && (
        <div
          className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200"
          data-testid={`review-note-banner-${session.id}`}
        >
          <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-1 flex items-center gap-1">
            <StickyNote size={12} /> Auditor Review Note
          </p>
          <p className="text-sm text-indigo-900 whitespace-pre-wrap">{session.review_note}</p>
          {session.review_note_by_name && (
            <p className="text-xs text-indigo-500 mt-1">
              — {session.review_note_by_name}
              {session.review_note_at && ` · ${new Date(session.review_note_at).toLocaleString()}`}
            </p>
          )}
        </div>
      )}

      {/* Yellow auditor-note banner — shown on Pending cards that came
          back from Ready-to-Pay with a revert_note attached. Helps the
          next auditor see *why* the session was bounced (e.g.
          "Distance looks padded — please re-review the GPS path")
          instead of starting from scratch. */}
      {session.status === 'pending_approval' && session.revert_note && (
        <div
          className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200"
          data-testid={`revert-note-banner-${session.id}`}
        >
          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">
            Sent back from Ready-to-Pay
          </p>
          <p className="text-sm text-amber-900 whitespace-pre-wrap">{session.revert_note}</p>
          {session.reverted_by_name && (
            <p className="text-xs text-amber-600 mt-1">
              — {session.reverted_by_name}
              {session.reverted_at && ` · ${new Date(session.reverted_at).toLocaleString()}`}
            </p>
          )}
        </div>
      )}
    </Card>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Audit Sessions</h1>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-sessions-btn"
        >
          <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Dedup-savings rollup — proves how much money the iter95
          server-side guard has saved over the last 30 days. Surfaces
          top offenders so admins can flag suspicious workers. */}
      {dedupSavings && !hideFinancials && (dedupSavings.total_savings > 0 || dedupSavings.sessions_with_drops > 0) && (
        <div
          className="rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 mb-6 flex items-start gap-4"
          data-testid="dedup-savings-banner"
        >
          <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700 shrink-0">
            <Shield size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-emerald-900">
              Dedup guard saved{' '}
              <span className="text-lg" data-testid="dedup-savings-total">
                ${dedupSavings.total_savings.toFixed(2)}
              </span>{' '}
              over the last {dedupSavings.window_days} days
            </p>
            <p className="text-sm text-emerald-800 mt-0.5">
              Refused to pay for <strong>{dedupSavings.total_dropped}</strong> duplicate deliveries across{' '}
              <strong>{dedupSavings.sessions_with_drops}</strong> of {dedupSavings.session_count} sessions
              (auto GPS credit + manual tap on the same house within 10 m).
            </p>

            {dedupSavings.top_offenders?.length > 0 && (
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer text-emerald-700 font-medium hover:text-emerald-900">
                  Top {dedupSavings.top_offenders.length} workers with the biggest discrepancy
                </summary>
                <ul className="mt-2 space-y-1.5 pl-1">
                  {dedupSavings.top_offenders.map((o) => (
                    <li
                      key={o.user_id}
                      className="flex items-center justify-between gap-3 py-1.5 border-b border-emerald-100/60 last:border-0"
                      data-testid={`dedup-offender-${o.user_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate flex items-center gap-1.5">
                          {o.name}
                          {o.needs_review && (
                            <Badge className="bg-red-100 text-red-700 text-[10px] py-0 px-1.5">
                              <AlertTriangle size={10} className="mr-0.5" />
                              REVIEW
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {o.session_count} sessions · claimed {o.total_claimed}, paid {o.total_paid}{' '}
                          ({(o.discrepancy_ratio * 100).toFixed(1)}% discrepancy)
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-emerald-700">
                          ${o.total_savings.toFixed(2)}
                        </p>
                        <p className="text-[10px] text-gray-500">saved</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        <Button
          variant={activeTab === 'pending' ? 'default' : 'outline'}
          onClick={() => setActiveTab('pending')}
          className={activeTab === 'pending' ? 'bg-yellow-600' : ''}
        >
          <Clock size={16} className="mr-2" />
          Pending ({pendingSessions.length})
        </Button>
        <Button
          variant={activeTab === 'approved' ? 'default' : 'outline'}
          onClick={() => setActiveTab('approved')}
          className={activeTab === 'approved' ? 'bg-blue-600' : ''}
        >
          <CheckCircle size={16} className="mr-2" />
          Ready to Pay ({approvedSessions.length})
        </Button>
        <Button
          variant={activeTab === 'declined' ? 'default' : 'outline'}
          onClick={() => setActiveTab('declined')}
          className={activeTab === 'declined' ? 'bg-red-600' : ''}
        >
          <XCircle size={16} className="mr-2" />
          Declined ({declinedSessions.length})
        </Button>
        <Button
          variant={activeTab === 'completed' ? 'default' : 'outline'}
          onClick={() => setActiveTab('completed')}
          className={activeTab === 'completed' ? 'bg-green-600' : ''}
        >
          <CheckCircle size={16} className="mr-2" />
          Completed ({completedSessions.length})
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <>
          {/* Pending Approval */}
          {activeTab === 'pending' && (
            <div className="space-y-4">
              {pendingSessions.length > 0 && (
                <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg px-4 py-3" data-testid="batch-approve-bar">
                  <label className="flex items-center gap-2 text-sm font-medium text-purple-900 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={pendingSessions.every(s => selectedIds.has(s.id))}
                      onChange={(e) => setSelectedIds(e.target.checked ? new Set(pendingSessions.map(s => s.id)) : new Set())}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      data-testid="select-all-pending-checkbox"
                    />
                    {selectedIds.size > 0
                      ? `${selectedIds.size} of ${pendingSessions.length} selected`
                      : `Select all (${pendingSessions.length} pending)`}
                  </label>
                  {selectedIds.size > 0 && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="batch-clear-btn">
                        Clear
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleBatchApproveSilently}
                        disabled={batchApproving}
                        className="bg-green-600 hover:bg-green-500"
                        data-testid="batch-approve-silently-btn"
                        title="Approve all selected sessions — workers receive no notification or email"
                      >
                        {batchApproving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle size={14} className="mr-1.5" />}
                        Approve Silently ({selectedIds.size})
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {pendingSessions.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-sm">
                  <Clock size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No sessions pending approval</p>
                </Card>
              ) : (
                pendingSessions.map(session => (
                  <SessionCard key={session.id} session={session} showActions={true} />
                ))
              )}
            </div>
          )}

          {/* Approved / Ready to Pay */}
          {activeTab === 'approved' && (
            <div className="space-y-4">
              {userType !== 'auditor' && approvedSessions.length > 1 && (
                <div className="flex justify-end mb-2">
                  <Button
                    onClick={handleSendAllToPayouts}
                    disabled={sendingToPayouts.all}
                    className="bg-purple-600 hover:bg-purple-500"
                    data-testid="send-all-payouts"
                  >
                    {sendingToPayouts.all ? <Loader2 size={14} className="animate-spin mr-2" /> : <ArrowRight size={14} className="mr-2" />}
                    Send All to Payouts ({approvedSessions.length})
                  </Button>
                </div>
              )}
              {approvedSessions.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-sm">
                  <CheckCircle size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No approved sessions waiting for payout</p>
                </Card>
              ) : (
                approvedSessions.map(session => (
                  <SessionCard key={session.id} session={session} />
                ))
              )}
            </div>
          )}

          {/* Declined */}
          {activeTab === 'declined' && (
            <div className="space-y-4">
              {declinedSessions.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-sm">
                  <XCircle size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No declined sessions</p>
                </Card>
              ) : (
                declinedSessions.map(session => (
                  <SessionCard key={session.id} session={session} showDeclineInfo />
                ))
              )}
            </div>
          )}

          {/* Completed (Paid / Pending Payout) */}
          {activeTab === 'completed' && (
            <div className="space-y-4">
              {completedSessions.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-sm">
                  <CheckCircle size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No completed sessions</p>
                </Card>
              ) : (
                completedSessions.map(session => (
                  <SessionCard key={session.id} session={session} />
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Decline Modal */}
      <Dialog open={!!declineModalOpen} onOpenChange={() => setDeclineModalOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Please provide a reason for declining this session:</p>
            <Textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Enter decline reason..."
              rows={3}
              data-testid="decline-reason-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDeclineModalOpen(null)}>Cancel</Button>
              <Button onClick={handleDeclineSession} disabled={declining} className="bg-red-600 hover:bg-red-500" data-testid="confirm-decline">
                {declining ? <Loader2 size={14} className="animate-spin mr-1" /> : <XCircle size={14} className="mr-1" />}
                Decline
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Revert-to-Pending Modal — opens from the Decline button on
          Ready-to-Pay cards. Lets the admin attach an optional auditor
          note explaining *why* the session was bounced, which renders
          as a yellow banner on the Pending card next time. */}
      <Dialog open={!!revertModal} onOpenChange={() => !revertingSession && setRevertModal(null)}>
        <DialogContent data-testid="revert-modal">
          <DialogHeader>
            <DialogTitle>Send Back to Pending Approval</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {revertModal && (
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{revertModal.job_title}</span> — {revertModal.user_name}
                <br />
                <span className="text-xs">The session will leave Ready-to-Pay and re-appear in the Pending tab for re-review.</span>
              </p>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Note for the next auditor <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <Textarea
                value={revertNote}
                onChange={(e) => setRevertNote(e.target.value)}
                placeholder="e.g. Distance looks padded — please re-review the GPS path"
                rows={3}
                maxLength={500}
                data-testid="revert-note-input"
                disabled={!!revertingSession}
              />
              <p className="text-xs text-gray-400 mt-1">{revertNote.length}/500 — will show as a yellow banner on the Pending card.</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setRevertModal(null)}
                disabled={!!revertingSession}
                data-testid="revert-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRevert}
                disabled={!!revertingSession}
                className="bg-red-600 hover:bg-red-500"
                data-testid="revert-confirm"
              >
                {revertingSession
                  ? <Loader2 size={14} className="animate-spin mr-1" />
                  : <XCircle size={14} className="mr-1" />
                }
                Send to Pending
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Note Modal — auditor leaves a note admins read before approving */}
      <Dialog open={!!noteModal} onOpenChange={() => !savingNote && setNoteModal(null)}>
        <DialogContent data-testid="review-note-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote size={18} className="text-indigo-600" /> Session Review Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {noteModal && (
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{noteModal.job_title}</span> — {noteModal.user_name}
                <br />
                <span className="text-xs">Shown to admins on this session's Pending card before they approve or decline.</span>
              </p>
            )}
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="e.g. GPS trail looks clean but delivery count seems high for the time on site — please double-check before approving"
              rows={4}
              maxLength={500}
              data-testid="review-note-input"
              disabled={savingNote}
            />
            <p className="text-xs text-gray-400">{noteText.length}/500</p>
            <div className="flex gap-2 justify-between">
              {noteModal?.review_note ? (
                <Button
                  variant="outline"
                  onClick={() => handleSaveReviewNote(true)}
                  disabled={savingNote}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="review-note-clear-btn"
                >
                  Clear Note
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setNoteModal(null)} disabled={savingNote} data-testid="review-note-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={() => handleSaveReviewNote(false)}
                  disabled={savingNote || !noteText.trim()}
                  className="bg-indigo-600 hover:bg-indigo-500"
                  data-testid="review-note-save-btn"
                >
                  {savingNote ? <Loader2 size={14} className="animate-spin mr-1" /> : <StickyNote size={14} className="mr-1" />}
                  Save Note
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayments;
